import { useState } from "react";

import i18next from "i18next";
import moment from "moment";
import { Link } from "react-router-dom";
import { BiChevronDown, BiChevronLeft, BiChevronRight } from "react-icons/bi";
import { RiFilter3Line, RiSearchLine, RiDownloadLine } from "react-icons/ri";
import { LuCheckCircle } from "react-icons/lu";

import { ContainerComponent, DotsLoadingComponent, TableComponent } from "../../ui";
import { useWindowDimensions } from "../../../hooks";

import { LoadingBlock } from "../../../pages/ResultPage/style";

import { normalizePrice } from "../../../utils";
import {
  FiltersCard,
  FiltersHeader,
  FiltersTitle,
  FiltersContent,
  FilterRow,
  FilterButtonsRow,
  FilterGroup,
  FilterLabel,
  FilterInputWrapper,
  FilterInput,
  FilterSelect,
  FilterDateInput,
  ApplyButton,
  ResetButton,
  ExportButton,
  NotFoundComponent,
  Subtitle,
  TitleComponent,
  Wrapper,
  PaginationInfo,
  PaginationPageButton,
  PaginationWrapper,
  StatusBadgeWrapper,
  TransactionsTableWrapper,
  FiltersToggleButton,
  FiltersToggleIcon,
  DesktopTableWrapper,
  MobileCardsWrapper,
  TransactionCard,
  CardHeader,
  CardMerchant,
  CardDateTime,
  CardAmount,
  CardDetails,
  CardDetailRow,
  CardDetailLabel,
  CardDetailValue,
  PaginationButtons,
} from "./style";

const TransactionsComponent = ({ isLoading, transactions, page, lastPage, request, total }) => {
  const { t } = i18next;
  const { width } = useWindowDimensions();
  const isMobile = width <= 830; // sm breakpoint

  const [currentPage, setCurrentPage] = useState(page || 1);
  const [filtersOpen, setFiltersOpen] = useState(!isMobile);
  const [filters, setFilters] = useState({
    order_id: "",
    type_operation: "",
    timestamp_start: "",
    timestamp_end: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({});

  const getOperationType = (type) => {
    if (type === "balance") return t("balance_operation");
    if (type === "order") return t("order_payment");
    return type || "-";
  };

  const CreatedTime = ({ createdAt }) => {
    const time = moment(createdAt);

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "2px", lineHeight: "1.4" }}>
        <div style={{ fontSize: "14px", color: "#333" }}>{time.format("DD.MM.YYYY")}</div>
        <div style={{ fontSize: "12px", color: "#666" }}>{time.format("HH:mm:ss")}</div>
      </div>
    );
  };

  const StatusBadge = ({ status }) => {
    if (status === "success" || status === "SUCCESS") {
      return (
        <StatusBadgeWrapper>
          <LuCheckCircle size={14} />
          <span>Success</span>
        </StatusBadgeWrapper>
      );
    }
    return <span>{status}</span>;
  };

  const handleChangePage = async (pageNum) => {
    await request({ ...appliedFilters, page: pageNum });
    setCurrentPage(pageNum);
  };

  const handleApplyFilters = async () => {
    setAppliedFilters(filters);
    await request({ ...filters, page: 1 });
    setCurrentPage(1);
  };

  const handleResetFilters = async () => {
    const emptyFilters = {
      order_id: "",
      type_operation: "",
      timestamp_start: "",
      timestamp_end: "",
    };
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    await request({ page: 1 });
    setCurrentPage(1);
  };

  const handleExport = () => {
    console.log("Export transactions", appliedFilters);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const getMerchantName = (paymentSystemName) => {
    if (!paymentSystemName) return "ADMIN";
    return paymentSystemName.toLowerCase() === "zplat" ? "zplat" : paymentSystemName;
  };

  const formatMobileDateTime = (createdAt) => {
    const time = moment(createdAt);
    return `${time.format("DD.MM.YYYY")} • ${time.format("HH:mm:ss")}`;
  };

  const limit = 10;
  const startItem = (currentPage - 1) * limit + 1;
  const endItem = Math.min(currentPage * limit, total || 0);

  const renderPagination = () => (
    <PaginationWrapper>
      <PaginationInfo>
        {t("showing")} {startItem}-{endItem} {t("of_total")} {total || 0} {t("transactions_count")}
      </PaginationInfo>
      <PaginationButtons>
        <PaginationPageButton
          disabled={currentPage === 1}
          onClick={() => handleChangePage(currentPage - 1)}
        >
          <BiChevronLeft />
          <span>{t("previous")}</span>
        </PaginationPageButton>
        {(() => {
          const pages = [];
          const maxPages = isMobile ? 3 : 5;
          let startPage = Math.max(1, currentPage - Math.floor(maxPages / 2));
          let endPage = Math.min(lastPage || 1, startPage + maxPages - 1);
          
          if (endPage - startPage < maxPages - 1) {
            startPage = Math.max(1, endPage - maxPages + 1);
          }
          
          for (let i = startPage; i <= endPage; i++) {
            pages.push(
              <PaginationPageButton
                key={i}
                $active={currentPage === i}
                onClick={() => handleChangePage(i)}
              >
                {i}
              </PaginationPageButton>
            );
          }
          return pages;
        })()}
        <PaginationPageButton
          disabled={currentPage === lastPage}
          onClick={() => handleChangePage(currentPage + 1)}
        >
          <span>{t("next")}</span>
          <BiChevronRight />
        </PaginationPageButton>
      </PaginationButtons>
    </PaginationWrapper>
  );

  return (
    <Wrapper>
      <ContainerComponent>
        <TitleComponent>{t("transactions")}</TitleComponent>
        <Subtitle>{t("view_manage_transactions")}</Subtitle>

        {isLoading && (
          <LoadingBlock>
            <DotsLoadingComponent />
          </LoadingBlock>
        )}

        {!isLoading && (
          <FiltersCard>
            <FiltersHeader onClick={() => isMobile && setFiltersOpen(!filtersOpen)} $clickable={isMobile}>
              <FiltersTitle>
                <RiFilter3Line size={20} />
                <span>{t("filters")}</span>
              </FiltersTitle>
              {isMobile && (
                <FiltersToggleIcon $open={filtersOpen}>
                  <BiChevronDown size={24} />
                </FiltersToggleIcon>
              )}
            </FiltersHeader>
            {(filtersOpen || !isMobile) && (
              <FiltersContent>
                <FilterRow>
                  <FilterGroup>
                    <FilterLabel>{t("order_num")}</FilterLabel>
                    <FilterInputWrapper>
                      <FilterInput
                        type="text"
                        placeholder={t("search_by_order")}
                        value={filters.order_id}
                        onChange={(e) => handleFilterChange("order_id", e.target.value)}
                        $hasIcon
                      />
                      <RiSearchLine style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#999", pointerEvents: "none", zIndex: 1 }} />
                    </FilterInputWrapper>
                  </FilterGroup>

                  <FilterGroup>
                    <FilterLabel>{t("operation_type")}</FilterLabel>
                    <FilterInputWrapper>
                      <FilterSelect
                        value={filters.type_operation}
                        onChange={(e) => handleFilterChange("type_operation", e.target.value)}
                      >
                        <option value="">{t("all_operations")}</option>
                        <option value="balance">{t("balance_operation")}</option>
                        <option value="order">{t("order_payment")}</option>
                      </FilterSelect>
                      <BiChevronDown style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", color: "#999", pointerEvents: "none" }} />
                    </FilterInputWrapper>
                  </FilterGroup>

                  <FilterGroup>
                    <FilterLabel>{t("date_from_d")}</FilterLabel>
                    <FilterInputWrapper>
                      <FilterDateInput
                        type="date"
                        value={filters.timestamp_start}
                        onChange={(e) => handleFilterChange("timestamp_start", e.target.value)}
                      />
                    </FilterInputWrapper>
                  </FilterGroup>

                  <FilterGroup>
                    <FilterLabel>{t("date_to_d")}</FilterLabel>
                    <FilterInputWrapper>
                      <FilterDateInput
                        type="date"
                        value={filters.timestamp_end}
                        onChange={(e) => handleFilterChange("timestamp_end", e.target.value)}
                      />
                    </FilterInputWrapper>
                  </FilterGroup>
                </FilterRow>
                <FilterButtonsRow>
                  <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                    <ApplyButton onClick={handleApplyFilters}>{t("apply_filters")}</ApplyButton>
                    <ResetButton onClick={handleResetFilters}>{t("reset")}</ResetButton>
                  </div>
                  <ExportButton onClick={handleExport}>
                    <RiDownloadLine size={18} />
                    <span>{t("export")}</span>
                  </ExportButton>
                </FilterButtonsRow>
              </FiltersContent>
            )}
          </FiltersCard>
        )}
      </ContainerComponent>

      <ContainerComponent>
        {(!isLoading && !transactions?.length) ? (
          <NotFoundComponent>{t("no_transactions")}</NotFoundComponent>
        ) : null}

        {(!isLoading && transactions?.length) ? (
          <>
            {/* Desktop Table View */}
            <DesktopTableWrapper>
              <TransactionsTableWrapper>
                <TableComponent
                  columns={[
                    { key: "created_at", label: t("date_and_time"), render: (column) => <CreatedTime createdAt={column.created_at} /> },
                    { key: "payment_system_name", label: t("merchant"), render: (column) => getMerchantName(column.payment_system_name) },
                    { key: "amount", label: t("amount"), render: (column) => `${normalizePrice(column?.amount)} ${column?.currency || "UZS"}` },
                    { key: "type_operation", label: t("type"), render: (column) => getOperationType(column.type_operation) },
                    { key: "payment_status", label: t("status"), render: (column) => <StatusBadge status={column.payment_status} /> },
                    { key: "order_id", label: t("order_num"), render: (column) => column.order_id ? <Link to={`/order/${column.order_id}/${column.session_id || ""}`}>{column.order_id}</Link> : "-" },
                    { key: "comment", label: t("comment"), render: (column) => column.comment || "-" },
                  ]}
                  data={transactions}
                  onChangePage={null}
                  prevButtonDisabled={true}
                  nextButtonDisabled={true}
                />
                {renderPagination()}
              </TransactionsTableWrapper>
            </DesktopTableWrapper>

            {/* Mobile Card View */}
            <MobileCardsWrapper>
              {transactions.map((transaction, index) => (
                <TransactionCard key={index}>
                  <CardHeader>
                    <div>
                      <CardMerchant>{getMerchantName(transaction.payment_system_name)}</CardMerchant>
                      <CardDateTime>{formatMobileDateTime(transaction.created_at)}</CardDateTime>
                    </div>
                    <StatusBadge status={transaction.payment_status} />
                  </CardHeader>
                  <CardAmount>
                    {normalizePrice(transaction?.amount)} {transaction?.currency || "UZS"}
                  </CardAmount>
                  <CardDetails>
                    <CardDetailRow>
                      <CardDetailLabel>{t("type")}:</CardDetailLabel>
                      <CardDetailValue>{getOperationType(transaction.type_operation)}</CardDetailValue>
                    </CardDetailRow>
                    {transaction.comment && (
                      <CardDetailRow>
                        <CardDetailLabel>{t("comment")}:</CardDetailLabel>
                        <CardDetailValue>{transaction.comment}</CardDetailValue>
                      </CardDetailRow>
                    )}
                    {transaction.order_id && (
                      <CardDetailRow>
                        <CardDetailLabel>{t("order_num")}:</CardDetailLabel>
                        <CardDetailValue>
                          <Link to={`/order/${transaction.order_id}/${transaction.session_id || ""}`}>
                            {transaction.order_id}
                          </Link>
                        </CardDetailValue>
                      </CardDetailRow>
                    )}
                  </CardDetails>
                </TransactionCard>
              ))}
              {renderPagination()}
            </MobileCardsWrapper>
          </>
        ) : null}
      </ContainerComponent>
    </Wrapper>
  );
};

export default TransactionsComponent;
